import { AI_USER_ACTION, type AiUserToolCall } from '@/lib/api/ai-user/ai-user-api'
import {
    getScreenController,
    waitForScreenController,
    type AiSchedulingServiceType,
    type SchedulingScreenController,
} from './screen-bridge'

/**
 * Executa, na tela de agendamento, as ações `scheduling_*` que o modelo pediu (ver
 * docs/ai-user.md, fase 2). Cada uma devolve um texto curto para o modelo: o que
 * aconteceu, os avisos da tela e o retrato atualizado.
 */

/** Ações que não encerram o passo: o resultado volta ao modelo. */
export const SCHEDULING_ACTION_NAMES: readonly string[] = [
    AI_USER_ACTION.schedulingOpen,
    AI_USER_ACTION.schedulingSearchCatalog,
    AI_USER_ACTION.schedulingAddItem,
    AI_USER_ACTION.schedulingRemoveItem,
    AI_USER_ACTION.schedulingChooseVariation,
    AI_USER_ACTION.schedulingImportOrder,
    AI_USER_ACTION.schedulingChoosePartner,
    AI_USER_ACTION.schedulingChooseProfessional,
    AI_USER_ACTION.schedulingApplyCoupon,
    AI_USER_ACTION.schedulingChargeSeparately,
    AI_USER_ACTION.schedulingChooseAddress,
    AI_USER_ACTION.schedulingChoosePayment,
    AI_USER_ACTION.schedulingPayLater,
    AI_USER_ACTION.schedulingSubmit,
    AI_USER_ACTION.schedulingPayOnBillingScreen,
]

export function isSchedulingAction(name: string): boolean {
    return SCHEDULING_ACTION_NAMES.includes(name)
}

/** Prazo para a tela de agendamento abrir e terminar de carregar. */
const OPEN_TIMEOUT_MS = 20_000
const READY_TIMEOUT_MS = 10_000
/** Resultados de busca devolvidos ao modelo: cada um custa tokens. */
const SEARCH_RESULTS_LIMIT = 15

const SERVICE_TYPES: readonly AiSchedulingServiceType[] = ['EXAM', 'CONSULTATION', 'PROCEDURE']
const PAYMENT_LABEL: Record<string, string> = {
    PIX: 'Pix',
    CREDIT_CARD: 'cartão de crédito',
    DEBIT_CARD: 'cartão de débito',
}
const SERVICE_TYPE_LABEL: Record<AiSchedulingServiceType, string> = {
    EXAM: 'exame',
    CONSULTATION: 'consulta',
    PROCEDURE: 'procedimento',
}

/**
 * Corta os resultados alternando os tipos (consulta, exame, procedimento), na ordem de
 * relevância de cada um. Cortar pela lista corrida deixava a consulta de fora: "obstet"
 * traz dezenas de ultrassons obstétricos antes de "Consulta Obstétrica".
 */
export function interleaveByServiceType<T extends { serviceType: string }>(results: T[], limit: number): T[] {
    const groups = new Map<string, T[]>()
    for (const result of results) {
        const group = groups.get(result.serviceType) ?? []
        group.push(result)
        groups.set(result.serviceType, group)
    }
    const queues = [...groups.values()]
    const picked: T[] = []
    while (picked.length < limit && queues.some(queue => queue.length > 0)) {
        for (const queue of queues) {
            const next = queue.shift()
            if (next && picked.length < limit) picked.push(next)
        }
    }
    return picked
}

function serviceTypeArg(args: Record<string, unknown>): AiSchedulingServiceType | null {
    const value = String(args.service_type ?? 'EXAM').toUpperCase()
    return (SERVICE_TYPES as readonly string[]).includes(value) ? (value as AiSchedulingServiceType) : null
}

export interface SchedulingActionContext {
    /** Cliente da conversa: é para ele que a tela abre. */
    clientId: number | null
    /** Abre a tela de agendamento (navegação da aplicação). */
    openScreen: (path: string) => void
    /**
     * Funil em que o envio entra: o da oportunidade da conversa (o backend reaproveita a
     * oportunidade aberta do mesmo funil em vez de abrir outra) ou o primeiro da IA.
     */
    pipelineId: number | null
    signal: AbortSignal
}

function describe(payload: unknown): string {
    return JSON.stringify(payload)
}

async function waitReady(controller: SchedulingScreenController, signal: AbortSignal): Promise<void> {
    const started = Date.now()
    while (!signal.aborted && Date.now() - started < READY_TIMEOUT_MS && !controller.getState().ready) {
        await new Promise(resolve => setTimeout(resolve, 100))
    }
}

/** A tela aberta para o cliente desta conversa, se houver. */
function currentScreen(clientId: number | null): SchedulingScreenController | undefined {
    const controller = getScreenController('scheduling')
    if (!controller || clientId == null) return undefined
    return controller.getState().client?.id === clientId ? controller : undefined
}

export async function executeSchedulingAction(
    call: AiUserToolCall,
    args: Record<string, unknown>,
    context: SchedulingActionContext,
): Promise<string> {
    const { clientId, signal } = context

    if (call.function.name === AI_USER_ACTION.schedulingOpen) {
        const serviceType = serviceTypeArg(args)
        if (!serviceType) {
            return describe({
                ok: false,
                messages: [`Erro: service_type inválido (${String(args.service_type)}). Use EXAM, CONSULTATION ou PROCEDURE.`],
            })
        }
        if (clientId == null) {
            return describe({
                ok: false,
                messages: ['Erro: esta conversa não tem cliente cadastrado. Peça os dados ao contato ou use ask_supervisor.'],
            })
        }
        let controller = currentScreen(clientId)
        if (!controller) {
            // Aberta para OUTRO cliente (a conversa anterior da fila): mudar só o
            // `clientId` da URL não remonta o formulário, e o cliente nunca troca.
            // Sai da tela e espera ela fechar, para abrir limpa.
            if (getScreenController('scheduling')) {
                context.openScreen('/')
                const leaving = Date.now()
                while (!signal.aborted && getScreenController('scheduling') && Date.now() - leaving < OPEN_TIMEOUT_MS) {
                    await new Promise(resolve => setTimeout(resolve, 100))
                }
            }
            context.openScreen(`/atendimento/scheduling?type=${serviceType}&clientId=${clientId}`)
            // A tela registra o controlador ao montar e só depois aplica o cliente da
            // URL: espera o registro e, em seguida, o cliente aparecer no retrato.
            const started = Date.now()
            await waitForScreenController('scheduling', () => true, OPEN_TIMEOUT_MS, signal)
            while (!signal.aborted && Date.now() - started < OPEN_TIMEOUT_MS) {
                controller = currentScreen(clientId)
                if (controller) break
                await new Promise(resolve => setTimeout(resolve, 100))
            }
        }
        if (!controller) return describe({ ok: false, messages: ['Erro: a tela de agendamento não abriu a tempo.'] })
        await waitReady(controller, signal)
        // Já aberta para o cliente, mas em outro tipo: troca pelo mesmo gesto da tela.
        if (controller.getState().serviceType !== serviceType) {
            return describe(await controller.setServiceType(serviceType))
        }
        return describe({ ok: true, messages: [], state: controller.getState() })
    }

    const controller = currentScreen(clientId)
    if (!controller) {
        return describe({ ok: false, messages: ['Erro: a tela de agendamento não está aberta. Use scheduling_open antes.'] })
    }

    switch (call.function.name) {
        case AI_USER_ACTION.schedulingSearchCatalog: {
            const found = await controller.searchCatalog(String(args.query ?? ''))
            return describe({ ...found, results: interleaveByServiceType(found.results, SEARCH_RESULTS_LIMIT) })
        }
        case AI_USER_ACTION.schedulingAddItem:
            return describe(await controller.addItem(Number(args.catalog_id)))
        case AI_USER_ACTION.schedulingRemoveItem:
            return describe(await controller.removeItem(Number(args.line)))
        case AI_USER_ACTION.schedulingChooseVariation:
            return describe(await controller.chooseVariation(Number(args.line), Number(args.variation_id)))
        case AI_USER_ACTION.schedulingImportOrder:
            return describe(await controller.importOrderFromImage(String(args.image_url ?? '')))
        case AI_USER_ACTION.schedulingChoosePartner:
            return describe(await controller.choosePartner(Number(args.partner_id)))
        case AI_USER_ACTION.schedulingChooseProfessional:
            return describe(await controller.chooseProfessional(Number(args.professional_id)))
        case AI_USER_ACTION.schedulingApplyCoupon:
            return describe(await controller.applyCoupon(String(args.code ?? '')))
        case AI_USER_ACTION.schedulingChargeSeparately:
            return describe(await controller.chargeSeparately())
        case AI_USER_ACTION.schedulingChooseAddress:
            return describe(await controller.chooseAddress(Number(args.address_id)))
        case AI_USER_ACTION.schedulingChoosePayment:
            return describe(
                await controller.choosePayment(
                    String(args.method ?? ''),
                    args.installments == null ? null : Number(args.installments),
                ),
            )
        case AI_USER_ACTION.schedulingPayLater:
            return describe(await controller.payLater())
        case AI_USER_ACTION.schedulingPayOnBillingScreen:
            return describe(await controller.payOnBillingScreen())
        case AI_USER_ACTION.schedulingSubmit:
            // Sem "Adicionar outro" a tela navega e sai do ar: o resultado já traz o que foi
            // criado e, se for o caso, que a forma se escolhe na tela da cobrança.
            return describe(await controller.submit(context.pipelineId, args.add_another === true))
        default:
            return describe({ ok: false, messages: [`Erro: ação desconhecida ${call.function.name}.`] })
    }
}

/** Uma linha para o Diário: o que a IA pediu na tela. */
export function describeSchedulingCall(name: string, args: Record<string, unknown>): string {
    switch (name) {
        case AI_USER_ACTION.schedulingOpen: {
            const type = serviceTypeArg(args)
            return `Abrindo o agendamento de ${type ? SERVICE_TYPE_LABEL[type] : 'exame'}.`
        }
        case AI_USER_ACTION.schedulingSearchCatalog:
            return `Procurando "${String(args.query ?? '')}" no catálogo.`
        case AI_USER_ACTION.schedulingAddItem:
            return `Adicionando o item ${String(args.catalog_id ?? '')}.`
        case AI_USER_ACTION.schedulingRemoveItem:
            return `Tirando o item da linha ${String(args.line ?? '')}.`
        case AI_USER_ACTION.schedulingChooseVariation:
            return `Escolhendo a variação ${String(args.variation_id ?? '')} da linha ${String(args.line ?? '')}.`
        case AI_USER_ACTION.schedulingImportOrder:
            return 'Lendo o pedido médico da imagem.'
        case AI_USER_ACTION.schedulingChoosePartner:
            return `Escolhendo o parceiro ${String(args.partner_id ?? '')}.`
        case AI_USER_ACTION.schedulingChooseProfessional:
            return `Escolhendo o profissional ${String(args.professional_id ?? '')}.`
        case AI_USER_ACTION.schedulingApplyCoupon:
            return `Aplicando o cupom ${String(args.code ?? '')}.`
        case AI_USER_ACTION.schedulingChargeSeparately:
            return 'Separando a cobrança desta venda.'
        case AI_USER_ACTION.schedulingChooseAddress:
            return `Escolhendo o endereço ${String(args.address_id ?? '')}.`
        case AI_USER_ACTION.schedulingChoosePayment: {
            const label = PAYMENT_LABEL[String(args.method ?? '').toUpperCase()] || String(args.method ?? '')
            const count = Number(args.installments)
            return `Escolhendo o pagamento: ${label}${count > 1 ? ` em ${count}x` : ''}.`
        }
        case AI_USER_ACTION.schedulingPayLater:
            return 'Marcando "gerar cobrança depois".'
        case AI_USER_ACTION.schedulingPayOnBillingScreen:
            return 'Deixando a forma de pagamento para a tela da cobrança.'
        case AI_USER_ACTION.schedulingSubmit:
            return args.add_another === true
                ? 'Enviando o agendamento e preparando o próximo na mesma cobrança.'
                : 'Enviando o agendamento.'
        default:
            return name
    }
}
